import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AgentList } from '@/components/agents/AgentList';
import { AgentTemplates } from '@/components/agents/AgentTemplates';
import { AgentAnalytics } from '@/components/agents/AgentAnalytics';
import { AgentExecutions } from '@/components/agents/AgentExecutions';
import { CreateAgentDialog } from '@/components/agents/CreateAgentDialog';
import { AgentBillIntegration } from '@/components/agents/AgentBillIntegration';
import { AgentScheduler } from '@/components/agents/AgentScheduler';
import { useAIAgents } from '@/hooks/useAIAgents';
import { Brain, Plus, Activity, TrendingUp, Clock } from 'lucide-react';

export default function AIAgents() {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const { agents, isLoading, useAgentAnalytics } = useAIAgents();
  const { data: analytics } = useAgentAnalytics();

  const activeAgents = agents.filter(agent => agent.status === 'active');
  const recentExecutions = agents.reduce((total, agent) => total + agent.execution_count, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="AI Agents"
        description="Manage and monitor your intelligent automation agents"
        action={
          <Button onClick={() => setShowCreateDialog(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create Agent
          </Button>
        }
      />

      {/* Analytics Overview */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Brain className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.total_agents || 0}</div>
            <p className="text-xs text-muted-foreground">
              {activeAgents.length} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Executions</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.total_executions || 0}</div>
            <p className="text-xs text-muted-foreground">
              {analytics?.successful_executions || 0} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.avg_success_rate ? `${Math.round(analytics.avg_success_rate)}%` : '0%'}
            </div>
            <p className="text-xs text-muted-foreground">
              Average across all agents
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${analytics?.total_cost ? (analytics.total_cost || 0).toFixed(2) : '0.00'}
            </div>
            <p className="text-xs text-muted-foreground">
              {analytics?.total_tokens_used || 0} tokens used
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="agents" className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="agents">My Agents</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="executions">Executions</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="integration">Bill Integration</TabsTrigger>
          <TabsTrigger value="scheduler">Scheduler</TabsTrigger>
        </TabsList>

        <TabsContent value="agents" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>My AI Agents</CardTitle>
                  <CardDescription>
                    Manage your custom AI agents and their configurations
                  </CardDescription>
                </div>
                <Badge variant="secondary">{agents.length} agents</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <AgentList />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Agent Templates</CardTitle>
              <CardDescription>
                Pre-built agent templates to get started quickly
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AgentTemplates />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="executions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>
                Monitor agent execution results and performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AgentExecutions />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <AgentAnalytics />
        </TabsContent>

        <TabsContent value="integration" className="space-y-4">
          <AgentBillIntegration />
        </TabsContent>

        <TabsContent value="scheduler" className="space-y-4">
          <AgentScheduler />
        </TabsContent>
      </Tabs>

      <CreateAgentDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
      />
    </div>
  );
}